'use client';

import Image from 'next/image';
import { useEffect, useRef } from 'react';
import type { Partner } from '@/data/resolve';
import { cn } from '@/lib/cn';

/**
 * Partner rail — official marks crossing the dark section on a lit band.
 *
 * Design: partner marks are corporate assets drawn for light ground, so the
 * rail gives them one — but as glass rather than paint. The band is let
 * through just enough for the brand bloom underneath to tint its middle and
 * for the navy to cool its edges, its own edges are hairlines of light, and
 * the marks run through it in ruled cells like entries in a register. Nothing
 * is recoloured or redrawn: each mark appears exactly as its owner publishes
 * it. Point at one and the rest of the register steps back, so the mark being
 * read is the only one fully present.
 *
 * Motion: one constant drift, and nothing else. The track used to take a
 * velocity injection from the page's own scrolling — it lurched left on the
 * way down, reversed on the way up and leaned into each burst. It read as a
 * fault, not as inertia. A register moves at the speed of a register: the
 * same, always. The only thing that changes it is a pointer resting on the
 * rail, which eases it to a stop so a mark can be read and clicked.
 *
 * The loop runs only while the rail is on screen in a visible tab, and
 * `prefers-reduced-motion` leaves it parked with every logo still legible.
 */

/** Drift, px/s — slow enough to read a wordmark as it passes. */
const DRIFT = 30;
/** How fast the rail eases to and from a stop, e-folds per second. */
const PARK = 5;
/**
 * Copies of the register in the track.
 *
 * The wrap point sits exactly one copy in, so everything from there to the
 * right edge of the viewport has to be drawn by the copies that follow: two
 * is not enough the moment the viewport is wider than a single copy, and the
 * rail runs off into empty band on a large display. Three covers a copy of
 * ~1.7k px on any realistic screen.
 */
const COPIES = 3;

function Register({
  items,
  decorative = false,
}: {
  items: readonly Partner[];
  decorative?: boolean;
}) {
  return (
    <ul aria-hidden={decorative || undefined} className="rail__row">
      {items.map((partner) => (
        <li key={partner.slug}>
          <a
            href={partner.href}
            target="_blank"
            rel="noreferrer noopener"
            tabIndex={decorative ? -1 : undefined}
            className="rail__cell"
          >
            {/* Light gathering under the mark being read. */}
            <span aria-hidden="true" className="rail__wash" />
            {/* The register line between entries. */}
            <span aria-hidden="true" className="rail__rule" />

            <Image
              src={partner.logo}
              alt={partner.logoAlt}
              width={640}
              height={260}
              loading="lazy"
              sizes="(min-width: 640px) 200px, 160px"
              className="rail__logo"
            />

            <span className="rail__name">{partner.name}</span>

            {/* Draws itself in under the entry. */}
            <span aria-hidden="true" className="rail__mark" />
          </a>
        </li>
      ))}
    </ul>
  );
}

export function PartnerRibbon({
  items,
  className,
}: {
  /** Partners already joined to the current language's role labels. */
  items: readonly Partner[];
  className?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  /** Set while a pointer rests on the rail or a cell holds focus. */
  const parked = useRef(false);

  useEffect(() => {
    const root = rootRef.current;
    const track = trackRef.current;
    if (!root || !track) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    /* The track holds identical copies, so wrapping at the width of one lands
       exactly on a seam and the rail never appears to jump. */
    let unit = track.scrollWidth / COPIES || 1;
    let offset = 0;
    /* 0 parked, 1 running — eased, so stopping and starting has weight. */
    let gate = 1;
    let last = 0;
    let raf = 0;

    const frame = (now: number) => {
      /* Capped so a backgrounded tab cannot resume with one huge step. */
      const dt = last ? Math.min((now - last) / 1000, 0.05) : 0;
      last = now;

      gate += ((parked.current ? 0 : 1) - gate) * (1 - Math.exp(-PARK * dt));
      offset = (offset + DRIFT * gate * dt) % unit;

      track.style.transform = `translate3d(${-offset}px,0,0)`;
      raf = requestAnimationFrame(frame);
    };

    const start = () => {
      if (raf) return;
      last = 0;
      raf = requestAnimationFrame(frame);
    };

    const stop = () => {
      if (!raf) return;
      cancelAnimationFrame(raf);
      raf = 0;
    };

    let onScreen = false;
    const sync = () => {
      if (onScreen && document.visibilityState === 'visible') start();
      else stop();
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        onScreen = Boolean(entry?.isIntersecting);
        sync();
      },
      { rootMargin: '150px 0px' },
    );
    io.observe(root);
    document.addEventListener('visibilitychange', sync);

    const ro = new ResizeObserver(() => {
      unit = track.scrollWidth / COPIES || 1;
    });
    ro.observe(track);

    return () => {
      stop();
      io.disconnect();
      ro.disconnect();
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  return (
    <div ref={rootRef} className={cn('rail', className)}>
      {/* Brand light spilling out from under the band. */}
      <span aria-hidden="true" className="rail__bloom" />

      <div
        className="rail__band"
        /* Pointer type checked, not just the event: a tap on a touch screen
           raises `mouseenter` and never raises `mouseleave` until something
           else is tapped, which would park the rail for good. */
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') parked.current = true;
        }}
        onPointerLeave={() => {
          parked.current = false;
        }}
        onFocusCapture={() => {
          parked.current = true;
        }}
        onBlurCapture={() => {
          parked.current = false;
        }}
      >
        {/* Hairlines of light along both edges, and one slow pass over the
            top one — the rail reading itself. */}
        <span aria-hidden="true" className="rail__edge rail__edge--top" />
        <span aria-hidden="true" className="rail__edge rail__edge--bottom" />
        <span aria-hidden="true" className="rail__scan" />

        {/* The fade is a frame around the track, not the band itself: the band
            keeps its crisp architectural edge across the full width while the
            marks dissolve before they reach it. The mask has to sit on this
            frame — the track is many times wider, so a mask of its own would
            fade at its far ends, way off screen. */}
        <div className="edge-fade [--edge:2.25rem] sm:[--edge:7rem]">
          <div ref={trackRef} className="rail__track">
            {Array.from({ length: COPIES }, (_, copy) => (
              <Register key={copy} items={items} decorative={copy > 0} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
